import { plural, t } from '@lingui/core/macro';

import { type AudienceGroupedOptionType } from '@/api/audience';
import { useOpportunityInfluenced } from '@/api/opportunity';
import {
  isPixelDetailLimitMet,
  usePixelImpressionTrend,
  usePixelReachEngagementTrend,
  usePixelTargetingEfficiency,
} from '@/api/pixel';
import { KpiMetric, KpiMetricGroup } from '@/components/kpi';
import { usePageFilterContext } from '@/components/page';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';
import { type WebTrafficSearchParamType } from '@/router/searchParams/webTrafficSearchParams';

type Props = {
  pixelIds?: string[];
  audience?: AudienceGroupedOptionType;
};

const PixelDetailKPIMetrics = ({ pixelIds, audience }: Props) => {
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate } = pageFilters;
  const audienceIds = audience?.audienceIds;

  const {
    pixelReachEngagementTrend,
    isLoading: isReachEngagementLoading,
    error: reachEngagementError,
  } = usePixelReachEngagementTrend(pixelIds, startDate, endDate, audienceIds);
  const {
    pixelImpressionTrend,
    isLoading: isImpressionTrendLoading,
    error: pixelImpressionError,
  } = usePixelImpressionTrend(pixelIds, startDate, endDate, audienceIds);
  const {
    pixelTargetingEfficiencyData,
    isLoading: isTargetingEfficiencyLoading,
    error: targetingEfficiencyError,
  } = usePixelTargetingEfficiency(pixelIds, startDate, endDate, audienceIds);
  const {
    opportunityInfluenced,
    isLoading: isOpportunitiesInfluencedLoading,
    error: oppInfluencedError,
  } = useOpportunityInfluenced(
    { startDate, endDate, audienceIds, pixelIds },
    isPixelDetailLimitMet(pixelIds),
  );

  const lastTrendItem = pixelReachEngagementTrend?.at(-1);
  const reached = lastTrendItem?.reach;
  const engaged = lastTrendItem?.engagement;
  const totalSize = lastTrendItem?.audienceSize;

  const defaultFilters: Partial<WebTrafficSearchParamType> = {
    'w.pixelIds': pixelIds,
    'w.audienceIds': audience?.audienceIds,
  };

  return (
    <KpiMetricGroup>
      <KpiMetric
        data-testid="metric-total-impressions"
        label={t`Total Impressions`}
        value={pixelImpressionTrend && numberFormat(pixelImpressionTrend.totalImpressions)}
        infoPopover={{
          title: t`Impressions (Pixel)`,
          body: t`Number of events the C99 Pixel recorded and analyzed. This only counts impressions where the Pixel code has been placed. This differs from the Impressions on the Vendors or Campaign page which gathers impression information directly from the Vendor Integration.`,
        }}
        isLoading={isImpressionTrendLoading}
        error={pixelImpressionError}
      />
      <KpiMetric
        data-testid="metric-targeting-efficiency"
        label={t`Efficiency`}
        value={
          pixelTargetingEfficiencyData &&
          numberFormat(
            pixelTargetingEfficiencyData.audience /
              Math.max(
                (pixelTargetingEfficiencyData.all ?? 0) -
                  (pixelTargetingEfficiencyData.unresolved ?? 0),
                1, // avoid division by zero
              ),
            { isPercent: true, precision: 1 },
          )
        }
        infoPopover={{
          title: t`Efficiency (Pixel)`,
          body: t`Number of impressions to an audience (TAM or Custom Audience) compared to the ALL impressions from the Pixel. Expressed as a percentage to show how well the vendor is serving impressions to the correct companies.`,
        }}
        isLoading={isTargetingEfficiencyLoading}
        error={targetingEfficiencyError}
      />
      <KpiMetric
        data-testid="metric-reached-companies"
        label={t`Reached Companies`}
        value={reached && numberFormat(reached)}
        valueLink={
          !!reached &&
          ROUTES.webTrafficActivityWithParams({
            ...defaultFilters,
            'w.metrics.impressions': '[1,]',
          })
        }
        caption={
          reached &&
          totalSize &&
          t`${numberFormat(reached / totalSize, { isPercent: true, precision: 1 })} of ${numberFormat(totalSize)}`
        }
        infoPopover={{
          title: t`Reached Companies`,
          body: t`Number of Companies within an audience which received at least one impression. The viewer does not have to visit the site to be "Reached" they only must view the content within the timeframe you have selected.`,
        }}
        isLoading={isReachEngagementLoading}
        error={reachEngagementError}
      />
      <KpiMetric
        data-testid="metric-engaged-companies"
        label={t`Engaged Companies`}
        value={engaged && numberFormat(engaged)}
        valueLink={
          !!engaged &&
          ROUTES.webTrafficActivityWithParams({
            ...defaultFilters,
            'w.metrics.impressions': '[1,]',
            'w.isEngaged': 'true',
          })
        }
        caption={
          engaged &&
          totalSize &&
          t`${numberFormat(engaged / totalSize, { isPercent: true, precision: 1 })} of ${numberFormat(totalSize)}`
        }
        infoPopover={{
          title: t`Engaged Companies`,
          body: t`Engaged companies are a count of companies which had at least one visit within the timeframe selected. Engaged companies within the Pixel section expands upon direct click through visits to include companies which have view-through as well.`,
        }}
        isLoading={isReachEngagementLoading}
        error={reachEngagementError}
      />
      <KpiMetric
        data-testid="metric-pipeline-influence"
        label={t`Pipeline Influence`}
        value={
          opportunityInfluenced &&
          numberFormat(opportunityInfluenced.pipeline.totalRevenue, {
            isCurrency: true,
            precision: 0,
          })
        }
        caption={plural(opportunityInfluenced?.pipeline.totalOpportunities || 0, {
          one: '# Opportunity',
          other: '# Opportunities',
        })}
        infoPopover={{
          title: t`Pipeline Influence (Pixel)`,
          body: t`Pipeline Influence includes opportunities opened within the opportunity influence window with at least one visit or view-through visit. This is an expanded form from the Vendors and Campaign Pages which uses click-through visits only.`,
        }}
        isLoading={isOpportunitiesInfluencedLoading}
        error={oppInfluencedError}
      />
      <KpiMetric
        data-testid="metric-closed-won-influence"
        label={t`Closed Won Influence`}
        value={
          opportunityInfluenced &&
          numberFormat(opportunityInfluenced.closedWon.totalRevenue, {
            isCurrency: true,
            precision: 0,
          })
        }
        caption={plural(opportunityInfluenced?.closedWon.totalOpportunities || 0, {
          one: '# Opportunity',
          other: '# Opportunities',
        })}
        infoPopover={{
          title: t`Closed Won Influence (Pixel)`,
          body: t`Closed Won Influence includes opportunities closed/won within dates selected with at least one visit or view-through visit. This is an expanded form from the Vendors and Campaign Pages which uses click-through visits only.`,
        }}
        isLoading={isOpportunitiesInfluencedLoading}
        error={oppInfluencedError}
      />
    </KpiMetricGroup>
  );
};

export default PixelDetailKPIMetrics;
